// @ts-nocheck
/**
 * A share-link recipient with no account. The token is limited to named
 * addresses, so the only way forward is to prove they hold one of them:
 * they type their email and we send a magic link back to this exact URL.
 */
import React, { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Mail, CheckCircle2 } from "lucide-react";

interface Props {
  /** Plain wording from the edge function about why they're blocked. */
  message: string;
  /** Shown so they know which sheet they're being asked to sign in for. */
  eventName?: string | null;
}

export default function ShareSignInGate({ message, eventName }: Props) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    const address = email.trim().toLowerCase();
    if (!address || !address.includes("@")) {
      setError("Please enter the email address the organiser shared this link with.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { error: err } = await supabase.auth.signInWithOtp({
        email: address,
        options: {
          // Land straight back on the sheet — no dashboard detour.
          emailRedirectTo: window.location.href,
          shouldCreateUser: true,
        },
      });
      if (err) throw err;
      setSent(true);
    } catch (err: any) {
      console.error("share sign-in: signInWithOtp failed", err);
      setError("We couldn't send the link just now. Please try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-sm rounded-md border bg-card p-6">
        {sent ? (
          <div className="space-y-3 text-center">
            <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-600" />
            <h1 className="text-base font-semibold">Check your email</h1>
            <p className="text-sm text-muted-foreground">
              We sent a sign-in link to <span className="font-medium">{email.trim()}</span>. Open it
              on this device and the sheet will load.
            </p>
            <Button variant="outline" className="w-full" onClick={() => setSent(false)}>
              Use a different address
            </Button>
          </div>
        ) : (
          <form onSubmit={send} className="space-y-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <h1 className="text-base font-semibold">Sign in to open this sheet</h1>
              </div>
              <p className="text-sm text-muted-foreground">
                {eventName ? `“${eventName}” is ` : "This sheet is "}shared with specific people.
                {" "}
                {message}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="share-email">Email address</Label>
              <Input
                id="share-email"
                type="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@club.com"
                className="min-h-[44px]"
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button type="submit" className="min-h-[44px] w-full" disabled={busy}>
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Send me a link
            </Button>
            <p className="text-xs text-muted-foreground">
              No password needed — the link signs you in and returns you here.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
